import {
  countOwnedBuildingsPerPlayer,
  capacityStatsForState,
  type Coord,
} from "@dailywars/core/engine";

import { GameCanvas } from "#/components/game/GameCanvas";
import type { ScrollChromeRefs } from "#/components/game/gameCanvas/boardViewport";
import type { GameCanvasViewState } from "#/components/game/gameCanvas/types";
import { GameHUD } from "#/components/game/GameHUD";
import { ObjectiveStatusBar } from "#/components/game/ObjectiveStatusBar";
import { OpponentTurnBanner } from "#/components/game/OpponentTurnBanner";
import { useCancelGameplaySelection } from "#/components/play/usePlayMapSession";
import {
  useAttackableTileKeys,
  useAttackableUnitIds,
  useAttackRangeFlashStyle,
  useAttackRangeFlashTileKeys,
  useBoardCursor,
  useCaptureBarHoverPreview,
  useHiddenCaptureBarTileKeys,
  usePathKeys,
  usePreviewAnimation,
  usePreviewUnit,
  useReachableKeys,
  useSelectedUnitId,
  useUnmovedHighlightFadeStartedAtMs,
  useUnmovedHighlightUnitIds,
} from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";

import { PlayMapCanvasViewportOverlay } from "./overlay";

export function PlayMapHudBar() {
  const localState = useGameSelector((s) => s.localState);
  const dayNumber = useGameSelector((s) => s.serverDayNumber);
  const buildingCounts = countOwnedBuildingsPerPlayer(localState.tiles);
  return (
    <>
      <GameHUD
        dayNumber={dayNumber}
        funds={localState.funds}
        capacityStats={capacityStatsForState(localState)}
        buildingCounts={buildingCounts}
      />
      <ObjectiveStatusBar state={localState} />
    </>
  );
}

function useGameCanvasViewState(): GameCanvasViewState {
  return {
    selectedUnitId: useSelectedUnitId(),
    reachableKeys: useReachableKeys(),
    pathKeys: usePathKeys(),
    previewUnit: usePreviewUnit(),
    previewAnimation: usePreviewAnimation(),
    attackableUnitIds: useAttackableUnitIds(),
    attackableTileKeys: useAttackableTileKeys(),
    attackRangeFlashTileKeys: useAttackRangeFlashTileKeys(),
    attackRangeFlashStyle: useAttackRangeFlashStyle(),
    unmovedHighlightUnitIds: useUnmovedHighlightUnitIds(),
    unmovedHighlightFadeStartedAtMs: useUnmovedHighlightFadeStartedAtMs(),
    hiddenCaptureBarTileKeys: useHiddenCaptureBarTileKeys(),
    captureBarHoverPreview: useCaptureBarHoverPreview(),
  };
}

export function PlayMapHudAndCanvas({
  boardScrollFocus,
  scrollChrome,
}: {
  boardScrollFocus: Coord | null;
  scrollChrome: ScrollChromeRefs;
}) {
  const localState = useGameSelector((s) => s.localState);
  const aiPlayback = useGameSelector((s) => s.aiPlayback);
  const animation = useGameSelector((s) => s.animation);
  const boardCursor = useBoardCursor();
  const onTileSelect = useGameSelector((s) => s.selectTile);
  const onTileHover = useGameSelector((s) => s.setHoveredTile);
  const onCancelSelection = useCancelGameplaySelection();
  const onAnimationComplete = useGameSelector((s) => s.completeAnimation);
  const viewState = useGameCanvasViewState();

  const status = localState.status;
  const playbackActive = Boolean(aiPlayback);
  return (
    <div className="relative w-full">
      {playbackActive && status === "in_progress" ? <OpponentTurnBanner /> : null}
      <div className="flex w-full justify-center">
        <div className="relative w-max max-w-full">
          <GameCanvas
            state={localState}
            viewState={viewState}
            animation={animation}
            cursorMode={boardCursor}
            onTileSelect={onTileSelect}
            onTileHover={onTileHover}
            onCancelSelection={onCancelSelection}
            onAnimationComplete={onAnimationComplete}
            scrollFocus={boardScrollFocus}
            scrollChrome={scrollChrome}
            viewportOverlay={
              <PlayMapCanvasViewportOverlay
                status={status}
                bottomDockRef={scrollChrome.bottomDockRef}
              />
            }
          />
        </div>
      </div>
    </div>
  );
}
